import { useLayoutEffect, useRef } from "react";
import { cx } from "@shared/cx";
import srOnly from "@shared/a11y.module.css";
import badge from "../../lib/status-badge.module.css";
import styles from "./EventHeader.module.css";
import { HeaderActions, type HeaderAction } from "../ui/HeaderActions";
import { Select } from "../ui/Select";
import { Tooltip } from "../ui/Tooltip";
import {
  PHASE_COLORS,
  PHASE_LABELS,
  PHASE_ORDER,
  type BookingPhase,
} from "../../lib/booking-phase";
import { useEditableField } from "../../lib/use-editable-field";
import { checkField, withinMaxLength } from "@shared/validators";
import type { HeaderField } from "../../lib/use-event-workspace";
import type { WorkspaceAssignment } from "../../lib/event-workspace-api";
import { InlineBanner } from "../ui/InlineBanner";

/** The header reads only the contractor names off the crew list. */
type Assignment = Pick<WorkspaceAssignment, "contractor">;

interface EventHeaderProps {
  eventName: string | null;
  quoteNumber: string;
  assignments: Assignment[];
  phase: BookingPhase;
  displayPhase?: BookingPhase;
  statusOverridePhase?: BookingPhase | null;
  busy: boolean;
  savingHeaderFields: Set<string>;
  savingStatusOverride?: boolean;
  onSaveField: (field: HeaderField, value: string | null) => void;
  onSaveStatusOverride?: (phase: BookingPhase | null) => void;
  onCancelClick: () => void;
  onBackClick: () => void;
}

const TEXT_MAX = 256;
const STATUS_OPTIONS: BookingPhase[] = [...PHASE_ORDER, "cancelled"];

export function EventHeader({
  eventName,
  quoteNumber,
  assignments,
  phase,
  displayPhase,
  statusOverridePhase,
  busy,
  savingHeaderFields,
  savingStatusOverride = false,
  onSaveField,
  onSaveStatusOverride,
  onCancelClick,
  onBackClick,
}: EventHeaderProps) {
  const assignedFirstNames = assignments
    .map((a) => a.contractor?.name?.split(" ")[0])
    .filter(Boolean)
    .join(", ");

  const name = useEditableField(eventName, {
    validate: (v) =>
      checkField(v, {
        predicate: (s) => withinMaxLength(s, TEXT_MAX),
        invalidMsg: `Keep this under ${TEXT_MAX} characters`,
      }),
    onSave: (v) => onSaveField("event_name", v),
  });

  const isSaving = savingHeaderFields.has("event_name");

  // The title is the page's H1 at the serif's 45px, so a real event name
  // does not fit one line of a phone, or of the column beside the actions.
  // It wraps instead of scrolling out of sight: a one-row textarea grown to
  // its text, its height measured and so an inline style (the exception
  // DESIGN-SYSTEM.md makes for a computed value).
  const nameRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    const fit = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    const ro =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(fit);
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [name.draft]);

  const actions: HeaderAction[] = [];
  if (phase !== "cancelled" && phase !== "done") {
    actions.push({
      kind: "button",
      label: "Cancel Event",
      onClick: onCancelClick,
      disabled: busy,
    });
  }
  actions.push({ kind: "button", label: "Back", onClick: onBackClick });

  // The phase badge is status, not nav — it stays visible at every width.
  const badgePhase = displayPhase ?? phase;
  const phaseBadge = onSaveStatusOverride ? (
    // The aria-label names the control; no tooltip repeats it.
    <Select
      variant="badge"
      value={statusOverridePhase ?? badgePhase}
      onChange={(next) =>
        onSaveStatusOverride(next === "" ? null : (next as BookingPhase))
      }
      disabled={savingStatusOverride || busy}
      // The badge keeps its phase colour; the trigger is the badge. The
      // `badge` variant carries no colour of its own, so the tone composes
      // without either side having to win a specificity contest.
      className={cx(badge.badge, PHASE_COLORS[badgePhase])}
      ariaLabel="Display status override"
      options={[
        { value: "", label: "Auto" },
        ...STATUS_OPTIONS.map((p) => ({ value: p, label: PHASE_LABELS[p] })),
      ]}
    />
  ) : (
    <span className={cx(badge.badge, PHASE_COLORS[badgePhase])}>
      <Tooltip content="Where this Booking sits in its lifecycle, derived from its dates, approval, and payments.">
        {PHASE_LABELS[badgePhase]}
      </Tooltip>
    </span>
  );

  return (
    <div className={styles.header}>
      {/* Canonical page H1 — sr-only; the visible title is the editable input. */}
      <h1 className={srOnly.srOnly}>{eventName || "Untitled Event"}</h1>
      <div className={styles.title}>
        <textarea
          ref={nameRef}
          rows={1}
          value={name.draft}
          // A name is one line however it wraps: Enter leaves the field,
          // which saves it, and a pasted line break becomes a space.
          onChange={(e) => name.setDraft(e.target.value.replace(/\s*\n\s*/g, " "))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          onFocus={name.onFocus}
          onBlur={name.onBlur}
          aria-label="Event name"
          aria-invalid={Boolean(name.error) || undefined}
          placeholder="Untitled Event"
          disabled={isSaving}
          className={cx(styles.nameInput, name.error && styles.nameInvalid)}
        />
        {name.error && (
          <InlineBanner variant="error" className={styles.nameError}>
            {name.error}
          </InlineBanner>
        )}
        <div className={styles.meta}>
          <p>
            Quote #{quoteNumber}
            {assignedFirstNames && ` · ${assignedFirstNames}`}
          </p>
        </div>
      </div>
      <HeaderActions actions={actions} badge={phaseBadge} />
    </div>
  );
}
